import React, { useState, useRef, useEffect } from 'react';

import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/* eslint-disable react-hooks/exhaustive-deps */

// Remove the default icon warning
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LAUNCH_DATE = new Date('2025-08-07T00:00:00Z');

// Calculate distance between two points using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in kilometers
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
};

const Game = () => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const mapRef = useRef(null);
  const [userPosition, setUserPosition] = useState(null); // last guess
  const [guesses, setGuesses] = useState([]); // {latlng, distance}

  const [distance, setDistance] = useState(null);
  
  const [feedback, setFeedback] = useState(''); // textual hint
  const [imageExpanded, setImageExpanded] = useState(true);
  const [countdown, setCountdown] = useState('');
  const [revealAnswer, setRevealAnswer] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const dayIndex = Math.floor((Date.now() - LAUNCH_DATE.getTime())/86400000);
  const storageKey = `btb-state-${dayIndex}`;

  useEffect(()=>{document.title = 'Beantown Browser';},[]);

  const ClickHandler = () => {
    useMapEvents({
      click: (e) => {
        if (!revealAnswer) {
          handleMapClick(e);
        }
      },
    });
    return null;
  };

  const fetchDailyLocation = async () => {
    try {
      // Use static JSON file instead of API
      const response = await fetch('/daily_location.json');
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      setCurrentLocation(data);
      
      // reset map view
      if (mapRef.current) {
        mapRef.current.setView([42.3601, -71.0589], 13);
      }
    } catch (error) {
      console.error('Error fetching location:', error);
    }
  };

  /* ----- persistence effects ----- */
  // restore saved state for today after location is loaded
  useEffect(()=>{
    if (currentLocation) {
      const raw = localStorage.getItem(storageKey);
      
      if(raw){
        try{
          const saved = JSON.parse(raw);
          if(saved){
            if(saved.guesses && saved.guesses.length > 0){
              setGuesses(saved.guesses.map(g=>({
                ...g,
                latlng: L.latLng(g.latlng.lat, g.latlng.lng)
              })));
              if(saved.guesses.length){
                setUserPosition(L.latLng(saved.guesses[saved.guesses.length-1].latlng.lat, saved.guesses[saved.guesses.length-1].latlng.lng));
              }
            }
            setDistance(saved.distance||null);
            setFeedback(saved.feedback||'');
            setRevealAnswer(saved.revealAnswer||false);
          }
        }catch(e){console.error('Failed to parse saved state',e);}
      } else {
        // Initialize fresh state if no saved data
        setUserPosition(null);
        setGuesses([]);
        setFeedback('');
        setDistance(null);
        setRevealAnswer(false);
      }
    }
  },[storageKey, currentLocation]);

  // save whenever relevant state changes
  useEffect(()=>{
    // Add a small delay to ensure state updates are complete
    const timeoutId = setTimeout(() => {
      const serializableGuesses = guesses.map(g=>({
        ...g,
        latlng:{lat:g.latlng.lat,lng:g.latlng.lng}
      }));
      const payload = {guesses:serializableGuesses,distance,feedback,revealAnswer};
      localStorage.setItem(storageKey, JSON.stringify(payload));
    }, 100);
    
    return () => clearTimeout(timeoutId);
  },[guesses,distance,feedback,revealAnswer,storageKey]);

  /* ----- effects ----- */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{
    // initial fetch
    fetchDailyLocation();
  },[]);

  useEffect(()=>{
    const updateCountdown = ()=>{
      const now = new Date();
      const next = new Date();
      next.setHours(24,0,0,0);
      const diff = next - now;
      const h = String(Math.floor(diff/3600000)).padStart(2,'0');
      const m = String(Math.floor((diff%3600000)/60000)).padStart(2,'0');
      const s = String(Math.floor((diff%60000)/1000)).padStart(2,'0');
      setCountdown(`${h}:${m}:${s}`);
    };
    updateCountdown();
    const id=setInterval(updateCountdown,1000);
    return ()=>clearInterval(id);
  },[]);

  const shareSymbols = {
    blue:'🟦', yellow:'🟨', orange:'🟧', red:'🟥', green:'🟩'
  };
  const arrowFromDir = {
    'north':'⬆️','south':'⬇️','east':'➡️','west':'⬅️',
    'north-east':'↗️','north-west':'↖️','south-east':'↘️','south-west':'↙️','':'⬜'
  };

  const handleShare = () => {
    const todayIndex = Math.floor((Date.now() - LAUNCH_DATE.getTime())/86400000);
    let text = `Beantown Browser #${todayIndex}\nWhere do you think this picture was taken?\n`;
    guesses.forEach((g,idx)=>{
      const color = getColor(g.distance);
      const sym = shareSymbols[color]||'⬜';
      const dir = directionFromGuess({lat:currentLocation.lat,lng:currentLocation.lng}, g.latlng);
      const arrow = revealAnswer && idx===guesses.length-1 && distance<=WIN_THRESHOLD_KM? '' : (arrowFromDir[dir]||'');
      text += `${sym}${arrow}\n`;
    });
    if(distance!==null){
      text += `\nI was ${formatDistance(distance)} away.`;
    }
    text += '\nhttps://beantown-browser.pages.dev/';
    navigator.clipboard.writeText(text).then(()=> setShareCopied(true));
  };

  const MAX_GUESSES = 5;
  const WIN_THRESHOLD_KM = 0.1524; // 500 ft ≈ 0.1524 km

  const directionFromGuess = (target, guess) => {
    const latDiff = target.lat - guess.lat;
    const lngDiff = target.lng - guess.lng;
    const absLat = Math.abs(latDiff);
    const absLng = Math.abs(lngDiff);
    const THRESH = 0.0003; // ~30 m
    // If both differences are tiny, user is very close
    if (absLat < THRESH && absLng < THRESH) return 'very close';

    // Decide dominant axis. If one axis difference is 1.5× the other, prefer that axis only.
    const dominantLat = absLat > absLng * 1.5;
    const dominantLng = absLng > absLat * 1.5;

    let parts = [];
    if (!dominantLng && absLat >= THRESH) parts.push(latDiff > 0 ? 'north' : 'south');
    if (!dominantLat && absLng >= THRESH) parts.push(lngDiff > 0 ? 'east' : 'west');

    return parts.join('-');
  };

  const getColor = (km) => {
    if (km === null || km === undefined) return 'gray';
    if (km <= WIN_THRESHOLD_KM) return 'green'; // correct within 500 ft
    if (km <= 0.3) return 'red';          // very close (< 0.3 km ~ 1000 ft)
    if (km <= 1)   return 'orange';       // closer (< 1 km)
    if (km <= 2)   return 'yellow';       // less far (< 2 km)
    return 'blue';                        // far
  };
  

  const memoizedIcons = {};
  const getIcon = (color) => {
    if (memoizedIcons[color]) return memoizedIcons[color];
    memoizedIcons[color] = L.divIcon({
      className: 'custom-marker',
      html: `<div style="background:${color};width:16px;height:16px;border-radius:50%;border:2px solid white"></div>`
    });
    return memoizedIcons[color];
  };

  const formatDistance = (km) => {
    const miles = km * 0.621371;
    if (miles >= 1) return `${miles.toFixed(2)} miles`;
    const feet = km * 3280.84;
    return `${Math.round(feet)} ft`;
  };

  const handleMapClick = (e) => {
    if (revealAnswer || guesses.length >= MAX_GUESSES) return;
    
    const guessIdx = guesses.length;
    const newGuess = { latlng: e.latlng, distance: null };
    
    // Add the new guess immediately
    setGuesses(prev => [...prev, newGuess]);
    setUserPosition(e.latlng);
    
    if (currentLocation) {
      // Calculate distance client-side
      const distance = calculateDistance(
        currentLocation.lat, 
        currentLocation.lng, 
        e.latlng.lat, 
        e.latlng.lng
      );
      
      // Update distance for this guess
      setGuesses(prev => prev.map((g,i)=> i===guessIdx ? {...g, distance:distance}: g));

      if (distance <= WIN_THRESHOLD_KM) {
        setDistance(distance);
        setFeedback('Correct!');
        setRevealAnswer(true);
      } else if (guessIdx + 1 >= MAX_GUESSES) {
        setDistance(distance);
        setFeedback('Out of guesses! Revealing answer.');
        setRevealAnswer(true);
      } else {
        const dir = directionFromGuess({ lat: currentLocation.lat, lng: currentLocation.lng }, e.latlng);
        setFeedback(`Try a bit more ${dir}.`);
      }
    }
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1>Beantown Browser</h1>
        <div className="countdown">Next puzzle in {countdown}</div>
      </div>

      <div className="map-container">
        <MapContainer 
          whenCreated={(map)=>{mapRef.current = map;}}
          center={[42.3601, -71.0589]} 
          zoom={13}
          style={{ height: '100dvh', width: '100vw' }}
        >
          <ClickHandler />
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          
          {revealAnswer && currentLocation && (
            <Marker position={[currentLocation.lat, currentLocation.lng]}>
              <Popup>
                {currentLocation.name}
              </Popup>
            </Marker>
          )}

          {guesses.map((g, idx) => (
            <Marker key={idx} position={g.latlng} icon={getIcon(getColor(g.distance))}>
              <Popup>
                {`Guess #${idx + 1}`}
                {revealAnswer && g.distance !== undefined && (
                  <p>Distance: {formatDistance(g.distance)}</p>
                )}
              </Popup>
            </Marker>
          ))}

          {/* dashed line after game ends */}
          {revealAnswer && userPosition && (
            <Polyline
              positions={[userPosition, [currentLocation.lat, currentLocation.lng]]}
              pathOptions={{ color: 'red', dashArray: '6' }}
            />
          )}
        </MapContainer>

        {feedback && !revealAnswer && (
          <div className="hint-overlay">{feedback}</div>
        )}

        {revealAnswer && (
          <div className="result-overlay">
            <h3>{feedback}</h3>
            {distance !== null && <p>You were {formatDistance(distance)} away</p>}
            <button onClick={handleShare}>Share</button>
            {shareCopied && <span style={{marginLeft:'8px'}}>Copied!</span>}
          </div>
        )}

        {currentLocation && (
          <>
            {!imageExpanded && (
              <img
                src={currentLocation.image}
                alt="location thumbnail"
                className="thumbnail-img"
                onClick={() => setImageExpanded(true)}
              />
            )}
            {imageExpanded && (
              <div className="img-overlay" onClick={() => setImageExpanded(false)}>
                <img
                  src={currentLocation.image}
                  alt="location"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Game;
